import type React from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import {
    initTokenRefreshScheduler,
    validateTokenAndRefreshIfNecessary,
} from '@routes/auth/___shared___/authTokenRefreshScheduler.ts';
import {
    cancelSessionExpiryScheduler,
    initSessionExpiryScheduler,
} from '@routes/auth/___shared___/sessionExpiryScheduler.ts';
import { SESSION_EXPIRY } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/pw-and-session/utils.ts';
import { COMMON_QUIZ_STATUS } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/quiz/___shared___/constants.ts';
import { getPolicyValueByNameAndUserType } from '@utils/auth.ts';

import type { WithChildrenProps } from './types.ts';

const RequireAuth: React.FC<WithChildrenProps> = ({ children }) => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const token = useAppSelector((state) => state.authLogin.token);
    const userFullInfo = useAppSelector((state) => state.user.userInfo);
    const userType = userFullInfo?.userType;
    const clientStatus = useAppSelector((state) => state.clientStatus.clientStatus);

    const currentFinalQuizStatus = useAppSelector((state) => state.learningFinalQuiz.currentFinalQuizStatus);
    const currentAdaptiveQuizStatus = useAppSelector((state) => state.learningAdaptiveQuiz.currentAdaptiveQuizStatus);

    const isQuizInProgress =
        currentFinalQuizStatus === COMMON_QUIZ_STATUS.STARTED ||
        currentFinalQuizStatus === COMMON_QUIZ_STATUS.IN_PROGRESS ||
        currentAdaptiveQuizStatus === COMMON_QUIZ_STATUS.STARTED ||
        currentAdaptiveQuizStatus === COMMON_QUIZ_STATUS.IN_PROGRESS;

    useEffect(() => {
        initTokenRefreshScheduler(token ?? null, dispatch);
    }, [token, dispatch]);

    useEffect(() => {
        void validateTokenAndRefreshIfNecessary(token ?? null, dispatch);
    }, [location, token, dispatch]);

    useEffect(() => {
        if (clientStatus && userType) {
            if (isQuizInProgress) {
                cancelSessionExpiryScheduler();
            } else {
                const sessionInactivityTimeoutMinutes = getPolicyValueByNameAndUserType(
                    SESSION_EXPIRY,
                    userType,
                    clientStatus,
                );

                initSessionExpiryScheduler(sessionInactivityTimeoutMinutes, dispatch);
            }
        }

        return () => {
            cancelSessionExpiryScheduler();
        };
    }, [dispatch, clientStatus, userType, isQuizInProgress]);

    return children;
};

export default RequireAuth;
